import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchHabits,
  createHabit,
  updateHabit,
  deleteHabit,
  toggleHabitLog,
  generateHabits,
} from '../../actions/planningAction';
import { flattenNodes } from './PlanningLibrary';
import './PlanningHabits.css';

// ── Date helpers (local-day, timezone-stable keys) ───────────────────────────
function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function todayKey() { return dateKey(new Date()); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

const WD_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const WD_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isExpectedOn(habit, d) {
  if (habit.scheduleType === 'daily') return true;
  if (habit.scheduleType === 'weekdays') return (habit.weekdays || []).includes(d.getDay());
  return true;
}
function scheduleLabel(h) {
  if (h.scheduleType === 'daily') return 'Daily';
  if (h.scheduleType === 'weekdays') {
    const days = (h.weekdays || []).slice().sort().map(d => WD_FULL[d].slice(0, 2)).join(' ');
    return days || 'Pick days';
  }
  return `${h.timesPerWeek}×/week`;
}
function computeStreak(habit, logSet) {
  if (habit.scheduleType === 'weekly') {
    const now = new Date(); const dow = now.getDay(); let count = 0;
    for (let i = 0; i <= dow; i++) if (logSet.has(dateKey(addDays(now, -i)))) count++;
    return count;
  }
  let streak = 0; let cursor = new Date();
  if (!logSet.has(todayKey())) cursor = addDays(cursor, -1);
  let guard = 0;
  while (guard++ < 400) {
    if (isExpectedOn(habit, cursor)) {
      if (logSet.has(dateKey(cursor))) streak++; else break;
    }
    cursor = addDays(cursor, -1);
  }
  return streak;
}
function completionRate(habit, logSet) {
  let expected = 0, done = 0; const now = new Date();
  for (let i = 0; i < 30; i++) {
    const d = addDays(now, -i);
    if (habit.scheduleType === 'weekly') { expected++; if (logSet.has(dateKey(d))) done++; }
    else if (isExpectedOn(habit, d)) { expected++; if (logSet.has(dateKey(d))) done++; }
  }
  return expected ? Math.round((done / expected) * 100) : 0;
}

// ── Full create/edit form (schedule + node link) ──────────────────────────────
function HabitForm({ initial, allNodes, onSave, onCancel }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [scheduleType, setScheduleType] = useState(initial?.scheduleType || 'daily');
  const [weekdays, setWeekdays] = useState(initial?.weekdays?.length ? initial.weekdays : [1, 2, 3, 4, 5]);
  const [timesPerWeek, setTimesPerWeek] = useState(initial?.timesPerWeek || 3);
  const [linkedNodeId, setLinkedNodeId] = useState(
    initial?.linkedNodeId ? String(initial.linkedNodeId) : (allNodes[0]?._id || '')
  );
  const toggleWd = (d) => setWeekdays(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d]);
  const submit = () => {
    if (!title.trim() || !linkedNodeId) return;
    onSave({
      title: title.trim(), scheduleType,
      weekdays: scheduleType === 'weekdays' ? weekdays : [],
      timesPerWeek: scheduleType === 'weekly' ? timesPerWeek : 3,
      linkedNodeId,
    });
  };
  return (
    <div className="ph-form">
      <input className="ph-form-title" placeholder="Habit name" value={title} autoFocus
        onChange={e => setTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
      <div className="ph-form-row">
        <span className="ph-form-label">Schedule</span>
        <div className="ph-seg">
          {['daily', 'weekdays', 'weekly'].map(s => (
            <button key={s} className={`ph-seg-btn${scheduleType === s ? ' ph-seg-btn--on' : ''}`} onClick={() => setScheduleType(s)}>
              {s === 'daily' ? 'Daily' : s === 'weekdays' ? 'Specific days' : 'X / week'}
            </button>
          ))}
        </div>
      </div>
      {scheduleType === 'weekdays' && (
        <div className="ph-form-row">
          <span className="ph-form-label">Days</span>
          <div className="ph-wd-pick">
            {WD_SHORT.map((w, i) => (
              <button key={i} className={`ph-wd${weekdays.includes(i) ? ' ph-wd--on' : ''}`} onClick={() => toggleWd(i)}>{w}</button>
            ))}
          </div>
        </div>
      )}
      {scheduleType === 'weekly' && (
        <div className="ph-form-row">
          <span className="ph-form-label">Target</span>
          <div className="ph-times">
            {[1, 2, 3, 4, 5, 6, 7].map(n => (
              <button key={n} className={`ph-wd${timesPerWeek === n ? ' ph-wd--on' : ''}`} onClick={() => setTimesPerWeek(n)}>{n}</button>
            ))}
            <span className="ph-times-suffix">× / week</span>
          </div>
        </div>
      )}
      <div className="ph-form-row">
        <span className="ph-form-label">Node *</span>
        <select className="ph-select" value={linkedNodeId} onChange={e => setLinkedNodeId(e.target.value)}>
          <option value="" disabled>Select a node…</option>
          {allNodes.map(n => (
            <option key={n._id} value={n._id}>{n.content.length > 50 ? n.content.slice(0, 50) + '…' : n.content}</option>
          ))}
        </select>
      </div>
      <div className="ph-form-actions">
        <button className="ph-form-cancel" onClick={onCancel}>Cancel</button>
        <button className="ph-form-save" onClick={submit} disabled={!title.trim() || !linkedNodeId}>{initial ? 'Save' : 'Add habit'}</button>
      </div>
    </div>
  );
}

// ── Habits page (table) ───────────────────────────────────────────────────────
export default function PlanningHabits({ visionId, milestones, theme, onFocusNode, onGoToPlan }) {
  const dispatch = useDispatch();
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);   // habit being edited (full form)
  const [showForm, setShowForm] = useState(false); // new-habit full form
  const [quickTitle, setQuickTitle] = useState('');
  const [quickNode, setQuickNode] = useState('');
  const [confirmDel, setConfirmDel] = useState(null); // habit pending delete
  const [aiSuggestions, setAiSuggestions] = useState(null); // [{...spec, _key}] | null
  const [aiPicked, setAiPicked] = useState(() => new Set());
  const [aiAdding, setAiAdding] = useState(false);

  const allNodes = useMemo(() => flattenNodes(milestones), [milestones]);
  const nodeById = useMemo(() => Object.fromEntries(allNodes.map(n => [n._id, n])), [allNodes]);

  useEffect(() => { if (!quickNode && allNodes[0]) setQuickNode(allNodes[0]._id); }, [allNodes, quickNode]);

  // Shared 7-day window (oldest → today) used as the table's day columns
  const week = useMemo(() => {
    const days = []; const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = addDays(now, -i);
      days.push({ key: dateKey(d), wd: WD_SHORT[d.getDay()], isToday: i === 0 });
    }
    return days;
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    dispatch(fetchHabits(visionId))
      .then(d => setHabits(d.habits || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch, visionId]);
  useEffect(() => { load(); }, [load]);

  const quickAdd = async () => {
    const t = quickTitle.trim();
    if (!t || !quickNode) return;
    setQuickTitle('');
    try {
      const d = await dispatch(createHabit(visionId, { title: t, scheduleType: 'daily', linkedNodeId: quickNode }));
      setHabits(p => [...p, d.habit]);
    } catch (_) {}
  };

  const create = async (data) => {
    try {
      const d = await dispatch(createHabit(visionId, data));
      setHabits(p => [...p, d.habit]);
      setShowForm(false);
    } catch (_) {}
  };

  const saveEdit = async (data) => {
    if (!editing) return;
    const id = editing._id;
    try {
      const d = await dispatch(updateHabit(id, data));
      setHabits(p => p.map(h => h._id === id ? { ...d.habit, logs: h.logs } : h));
      setEditing(null);
    } catch (_) {}
  };

  const remove = async (habit) => {
    setConfirmDel(null);
    setHabits(p => p.filter(h => h._id !== habit._id));
    try { await dispatch(deleteHabit(habit._id)); } catch (_) { load(); }
  };

  const toggle = async (habit, date) => {
    setHabits(p => p.map(h => {
      if (h._id !== habit._id) return h;
      const set = new Set(h.logs || []);
      set.has(date) ? set.delete(date) : set.add(date);
      return { ...h, logs: [...set] };
    }));
    try { await dispatch(toggleHabitLog(habit._id, date)); } catch (_) { load(); }
  };

  const generate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const d = await dispatch(generateHabits(visionId));
      const sugs = (d.suggestions || []).map((s, i) => ({ ...s, _key: `s_${i}` }));
      if (sugs.length === 0) {
        window.alert('Your habits already cover the plan — nothing new to suggest.');
      } else {
        setAiSuggestions(sugs);
        setAiPicked(new Set(sugs.map(s => s._key))); // all selected by default
      }
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Could not generate habits.');
    }
    setGenerating(false);
  };

  const togglePick = (key) => {
    setAiPicked(p => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const addPicked = async () => {
    if (aiAdding || !aiSuggestions) return;
    const chosen = aiSuggestions.filter(s => aiPicked.has(s._key));
    if (!chosen.length) { setAiSuggestions(null); return; }
    setAiAdding(true);
    const created = [];
    for (const s of chosen) {
      try {
        const d = await dispatch(createHabit(visionId, {
          title: s.title,
          scheduleType: s.scheduleType,
          weekdays: s.weekdays,
          timesPerWeek: s.timesPerWeek,
          linkedNodeId: s.linkedNodeId,
        }));
        created.push(d.habit);
      } catch (_) {}
    }
    if (created.length) setHabits(p => [...p, ...created]);
    setAiAdding(false);
    setAiSuggestions(null);
  };

  const doneTodayCount = useMemo(() => {
    const tk = todayKey();
    return habits.filter(h => (h.logs || []).includes(tk)).length;
  }, [habits]);

  return (
    <div className={`ph-wrap${theme === 'light' ? ' ph-wrap--light' : ''}`}>
      {generating && (
        <div className="ph-thinking">
          <div className="ph-thinking-card">
            <span className="ph-thinking-spark">✦</span>
            <div className="ph-thinking-text">Dmyor Assist is building your habits…</div>
            <div className="ph-thinking-sub">Reading your plan to find the behaviours that matter</div>
          </div>
        </div>
      )}

      {aiSuggestions && (
        <div className="ph-review-overlay" onMouseDown={() => !aiAdding && setAiSuggestions(null)}>
          <div className="ph-review" onMouseDown={e => e.stopPropagation()}>
            <div className="ph-review-head">
              <div>
                <h3 className="ph-review-title">✦ Suggested habits</h3>
                <p className="ph-review-sub">Pick the ones you want. {aiPicked.size} of {aiSuggestions.length} selected.</p>
              </div>
            </div>
            <div className="ph-review-list">
              {aiSuggestions.map(s => {
                const on = aiPicked.has(s._key);
                return (
                  <div key={s._key} className={`ph-review-item${on ? ' ph-review-item--on' : ''}`}>
                    <button className="ph-review-pick" onClick={() => togglePick(s._key)}>
                      <span className={`ph-review-check${on ? ' ph-review-check--on' : ''}`}>{on ? '✓' : ''}</span>
                      <span className="ph-review-body">
                        <span className="ph-review-name">{s.title}</span>
                        <span className="ph-review-meta">{scheduleLabel(s)}</span>
                      </span>
                    </button>
                    <select
                      className="ph-review-node"
                      value={s.linkedNodeId || ''}
                      onChange={e => setAiSuggestions(prev => prev.map(x => x._key === s._key ? { ...x, linkedNodeId: e.target.value } : x))}
                      title="Linked node"
                    >
                      {allNodes.map(n => (
                        <option key={n._id} value={n._id}>{n.content.length > 36 ? n.content.slice(0, 36) + '…' : n.content}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
            <div className="ph-review-actions">
              <button className="ph-form-cancel" onClick={() => setAiSuggestions(null)} disabled={aiAdding}>Cancel</button>
              <button className="ph-form-save" onClick={addPicked} disabled={aiAdding || aiPicked.size === 0}>
                {aiAdding ? 'Adding…' : `Add ${aiPicked.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="ph-confirm-overlay" onMouseDown={() => setConfirmDel(null)}>
          <div className="ph-confirm" onMouseDown={e => e.stopPropagation()}>
            <h3 className="ph-confirm-title">Delete this habit?</h3>
            <p className="ph-confirm-text">
              "{confirmDel.title}" and its tracking history will be permanently removed. This cannot be undone.
            </p>
            <div className="ph-confirm-actions">
              <button className="ph-confirm-cancel" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="ph-confirm-go" onClick={() => remove(confirmDel)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="ph-inner">
        <div className="ph-header">
          <div>
            <h1 className="ph-heading">Habits</h1>
            <p className="ph-sub">
              {habits.length > 0 ? `${doneTodayCount}/${habits.length} done today` : 'Daily activities that build this vision over time.'}
            </p>
          </div>
          <div className="ph-header-actions">
            <button className="ph-ai-btn" onClick={generate} disabled={generating}>
              {generating ? '✦ Dmyor Assist is thinking…' : '✦ Dmyor Assist'}
            </button>
            <button className="ph-new-btn" onClick={() => { setShowForm(true); setEditing(null); }}>+ New habit</button>
          </div>
        </div>

        {showForm && <HabitForm allNodes={allNodes} onSave={create} onCancel={() => setShowForm(false)} />}

        {!loading && allNodes.length === 0 && (
          <div className="ph-no-nodes">
            <div className="ph-no-nodes-card">
              <div className="ph-no-nodes-icon">↻</div>
              <h2 className="ph-no-nodes-title">Build your plan first</h2>
              <p className="ph-no-nodes-desc">
                Habits are tied to nodes in your plan. Add at least one milestone or action in the Plan view, then come back to set up daily habits around it.
              </p>
              <button className="ph-no-nodes-cta" onClick={() => onGoToPlan?.()}>
                Go to Plan →
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="ph-empty"><span className="ph-spinner" /> Loading…</div>
        ) : allNodes.length === 0 ? null : (
          <div className="ph-table-wrap">
            <table className="ph-table">
              <thead>
                <tr>
                  <th className="ph-th-check"></th>
                  <th className="ph-th-name">Habit</th>
                  <th className="ph-th-sched">Schedule</th>
                  {week.map(d => (
                    <th key={d.key} className={`ph-th-day${d.isToday ? ' ph-th-day--today' : ''}`}>{d.wd}</th>
                  ))}
                  <th className="ph-th-streak">Streak</th>
                  <th className="ph-th-rate">30d</th>
                  <th className="ph-th-act"></th>
                </tr>
              </thead>
              <tbody>
                {habits.map(h => {
                  if (editing && editing._id === h._id) {
                    return (
                      <tr key={h._id}>
                        <td colSpan={7 + 5}>
                          <HabitForm initial={h} allNodes={allNodes} onSave={saveEdit} onCancel={() => setEditing(null)} />
                        </td>
                      </tr>
                    );
                  }
                  const logSet = new Set(h.logs || []);
                  const node = h.linkedNodeId ? nodeById[String(h.linkedNodeId)] : null;
                  const streak = computeStreak(h, logSet);
                  const rate = completionRate(h, logSet);
                  const streakTxt = h.scheduleType === 'weekly' ? `${streak}/${h.timesPerWeek}` : `${streak}`;
                  const tk = todayKey();
                  return (
                    <tr key={h._id} className="ph-tr" style={{ '--habit-color': '#3b82f6' }}>
                      <td className="ph-td-check">
                        <button className={`ph-check${logSet.has(tk) ? ' ph-check--on' : ''}`} onClick={() => toggle(h, tk)} title="Today">
                          {logSet.has(tk) && <span className="ph-tick" />}
                        </button>
                      </td>
                      <td className="ph-td-name">
                        <span className="ph-name">{h.title}</span>
                        {node && (
                          <button
                            className="ph-linked"
                            onClick={() => onFocusNode?.(String(h.linkedNodeId))}
                            title="Open node in planner"
                          >↳ {node.content.length > 28 ? node.content.slice(0, 28) + '…' : node.content}</button>
                        )}
                      </td>
                      <td className="ph-td-sched">{scheduleLabel(h)}</td>
                      {week.map(d => {
                        const expected = isExpectedOn(h, new Date(d.key));
                        const done = logSet.has(d.key);
                        return (
                          <td key={d.key} className="ph-td-day">
                            <button
                              className={`ph-cell${done ? ' ph-cell--done' : ''}${expected ? '' : ' ph-cell--off'}${d.isToday ? ' ph-cell--today' : ''}`}
                              onClick={() => toggle(h, d.key)}
                              title={d.key}
                            >{done && <span className="ph-tick" />}</button>
                          </td>
                        );
                      })}
                      <td className="ph-td-streak">{streak > 0 ? <span className="ph-streak">🔥 {streakTxt}</span> : <span className="ph-dash">—</span>}</td>
                      <td className="ph-td-rate">{rate}%</td>
                      <td className="ph-td-act">
                        <button className="ph-icon-btn" onClick={() => { setEditing(h); setShowForm(false); }} title="Edit"><i className="bx bx-pencil" /></button>
                        <button className="ph-icon-btn ph-icon-btn--del" onClick={() => setConfirmDel(h)} title="Delete"><i className="bx bx-trash" /></button>
                      </td>
                    </tr>
                  );
                })}

                {/* Quick add row */}
                <tr className="ph-add-row">
                  <td className="ph-td-check"><span className="ph-add-plus">+</span></td>
                  <td colSpan={7 + 4}>
                    <div className="ph-quick-wrap">
                      <input
                        className="ph-quick-input"
                        placeholder={allNodes.length ? 'Add a habit and press Enter (daily — edit for a schedule)' : 'Create nodes in the planner first'}
                        value={quickTitle}
                        disabled={!allNodes.length}
                        onChange={e => setQuickTitle(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') quickAdd(); }}
                      />
                      {allNodes.length > 0 && (
                        <select className="ph-quick-node" value={quickNode} onChange={e => setQuickNode(e.target.value)} title="Link to node">
                          {allNodes.map(n => (
                            <option key={n._id} value={n._id}>{n.content.length > 40 ? n.content.slice(0, 40) + '…' : n.content}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
